import { from, Observable } from "rxjs";
import { PageRequest } from "src/app/support/page-request";
import { TaxRate } from "../../models/tax-rate/tax-rate";
import { TaxRateCreate } from "../../models/tax-rate/tax-rate.create";
import { TaxRateDelete } from "../../models/tax-rate/tax-rate.delete";
import { TaxRateLoad } from "../../models/tax-rate/tax-rate.load";
import { TaxRateBackend } from "./tax-rate.backend";
import { rxRandomErrorDelayAsync } from "src/app/support/rx-random-error-delay";
import { map, switchMap } from "rxjs/operators";
import { find } from "lodash-es";
import { Paginated } from "src/app/support/paginated";
import { uuid, UUID } from "src/app/support/uuid";
import { UserNotify } from "src/app/support/notify-decorator";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import {
  loadDemoStorage,
  writeDemoStorage
} from "src/app/support/demo-storage";

const DB_NAME = "tax-rate";

export class TaxRateMockBackend extends TaxRateBackend {
  load(request: TaxRateLoad): Observable<TaxRate> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => {
        return find(list, (x) => x.uuid === request.uuid)!;
      })
    );
  }

  list(request: PageRequest): Observable<Paginated<TaxRate>> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => {
        return <Paginated<TaxRate>>{
          list,
          count: list.length
        };
      })
    );
  }

  @UserNotify({
    successMessage: marker("Aliquota iva creata"),
    errorMessage: marker("Impossibile creare l'aliquota iva")
  })
  create(request: TaxRateCreate): Observable<UUID> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        const newUuid = uuid();

        const newTaxRate: Partial<TaxRate> = {
          uuid: newUuid,
          ...request
        };

        return from(writeDemoStorage(DB_NAME, [...list, newTaxRate])).pipe(
          map(() => newUuid)
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Aliquota iva aggiornata"),
    errorMessage: marker("Impossibile aggiornare l'aliquota iva")
  })
  update(request: TaxRate): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.map((x) => {
              if (x.uuid === request.uuid) {
                return {
                  ...x,
                  ...request
                };
              }
              return x;
            })
          )
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Aliquota iva rimossa"),
    errorMessage: marker("Impossibile rimuovere l'aliquota iva")
  })
  delete(request: TaxRateDelete): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.filter((x) => x.uuid !== request.uuid)
          )
        );
      })
    );
  }
}

async function loadDb(): Promise<TaxRate[]> {
  return (await loadDemoStorage<TaxRate[]>(DB_NAME)) || [];
}
